import './LayoutMain.scss';
import { FC, ReactNode, useMemo } from 'react';
// eslint-disable-next-line no-restricted-imports -- каркас закрывает временную панель по ключу перехода и выбирает раскладку дашборда
import { useLocation } from 'react-router';
import AppShell, { SidebarMascots } from '@srm/ui/shell/AppShell';
import Folders from '@molecules/Folders/Folders';
import { useActions } from '@hooks/useActions';
import { useTypedSelector } from '@hooks/useTypedSelector';
import ReleaseNotes from '@molecules/ReleaseNotes/ReleaseNotes';
import SupportSessionBanner from '@molecules/SupportSessionBanner/SupportSessionBanner';
import ScreenShareBar from '@molecules/ScreenShareBar';
import SupportRequestBars from '@molecules/SupportRequestBars';
import BrowserSupportBanner from '@molecules/BrowserSupportBanner';
import TourProvider from '@molecules/Tour/TourProvider';
import StartupScreen from '@organisms/StartupScreen/StartupScreen';

export interface ILayoutMainProps {
    /** Наполнение */
    children: ReactNode;
    /** Панель вверху */
    header?: ReactNode;
    /** Левое меню */
    sidebar?: ReactNode;
}

const NOTES = (
    <>
        <ScreenShareBar />
        <SupportSessionBanner />
        <SupportRequestBars />
        <BrowserSupportBanner />
        <ReleaseNotes />
    </>
);
const STARTUP = <StartupScreen withinLayout />;
const OVERLAYS = <TourProvider />;
const SIDEBAR_DECORATION = <SidebarMascots />;

/**
 * Оболочка Front: каркас `@srm/ui/shell/AppShell`, меню разделов, плашки браузера и релиз-новостей, туры.
 * Полосы сеанса поддержки (показ экрана, вход «от имени», запросы поддержки) — первыми в `notes`: под шапкой, вне
 * прокрутки страницы. Переход перерисовывает только каркас: слоты — те же элементы, их содержимое следит за адресом
 * само.
 */
const LayoutMain: FC<ILayoutMainProps> = ({ children, header, sidebar }: ILayoutMainProps) => {
    const userId = useTypedSelector((state) => state.auth.user.id);
    // Закрепление меню живёт в сторе: по нему дашборд держит ширину сетки на время анимации панели.
    const isMenuOpen = useTypedSelector((state) => state.mainLayout.isMenuOpen);
    const { toggleMenu } = useActions();
    const { pathname, key: locationKey } = useLocation();
    // У шаблонов дашбордов есть телефонная раскладка; остальные страницы держат десктопную ширину.
    const isDashboard = pathname === '/dashboards' || pathname.startsWith('/dashboards/');
    const folders = useMemo(() => <Folders key={userId} />, [userId]);

    return (
        <AppShell
            className={isDashboard ? 'app-shell--dashboard' : undefined}
            phoneLayout={isDashboard}
            locationKey={locationKey}
            pinned={isMenuOpen}
            onPinnedChange={toggleMenu}
            header={header}
            sidebarDecoration={sidebar ? undefined : SIDEBAR_DECORATION}
            sidebar={sidebar ?? folders}
            notes={NOTES}
            startup={STARTUP}
            overlays={OVERLAYS}
        >
            {children}
        </AppShell>
    );
};

export default LayoutMain;
